import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { 
  FiSearch, FiFilter, FiBookmark, FiShare2, 
  FiHeart, FiClock, FiChevronLeft, FiChevronRight,
  FiX, FiMessageCircle, FiRefreshCw, FiTag, FiCalendar,
  FiArrowUp, FiTrash2, FiEdit3
} from 'react-icons/fi';
import blogService from "../../services/blogService";
import favoritesService from "../../services/favoritesService";
import { getUserId, getUserEmail, isAuthenticated } from "../../utils/authUtils";
import lavenderOil from "../../images/doterra/Lavender.png";
import peppermintOil from "../../images/doterra/Peppermint.png";
import lemonOil from "../../images/doterra/Lemon.png";
import "./modern-blog-styles.css";

const ADMIN_EMAILS = [
  process.env.REACT_APP_ADMIN,
  process.env.REACT_APP_ARI_ADMIN,
]
  .filter(Boolean)
  .map((email) => email.trim().toLowerCase());

const ModernBlogList = (props) => {
  // Core State
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter and Search State
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [readingTimeFilter, setReadingTimeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 9;
  
  // User Interactions
  const [userFavorites, setUserFavorites] = useState(new Set());
  const [userLikes, setUserLikes] = useState(new Set());
  
  const { user } = props.user || {};
  const userId = getUserId(props.user);

  const userEmail = getUserEmail(props.user);
  const isUserAuthenticated = isAuthenticated(props.user);

  // Check if user is admin
  const isAdmin = useCallback(() => {
    return ADMIN_EMAILS.includes(String(user?.email || "").trim().toLowerCase());
  }, [user?.email]);

  // Debug logging
  useEffect(() => {
    console.log('ModernBlogList - User debug:', {
      propsUser: props.user,
      user: user,
      userId: userId,
      userEmail: userEmail,
      isUserAuthenticated: isUserAuthenticated
    });
  }, [props.user, user, userId, userEmail, isUserAuthenticated]);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [blogsResponse, categoriesResponse] = await Promise.all([
        blogService.getBlogs(),
        blogService.getBlogCategories()
      ]);

      // Process blogs data
      const blogsData = Array.isArray(blogsResponse) ? blogsResponse : blogsResponse?.data || [];
      const enhancedBlogs = blogsData.map(blog => blogService.formatBlogDataSync(blog));
      
      // Fetch real comment counts for all blogs asynchronously
      const blogsWithRealComments = await Promise.all(
        enhancedBlogs.map(async (blog) => {
          try {
            const commentCountData = await blogService.getCommentCount(blog.id);
            return {
              ...blog,
              comments: commentCountData.totalComments || blog.comments || 0,
              formattedComments: blogService.formatNumber(commentCountData.totalComments || blog.comments || 0)
            };
          } catch (error) {
            console.warn(`Could not fetch comment count for blog ${blog.id}:`, error);
            return blog;
          }
        })
      );
      
      setBlogs(blogsWithRealComments);
      
      // Process categories data
      const categoriesData = Array.isArray(categoriesResponse) ? categoriesResponse : categoriesResponse?.data || [];
      setCategories(categoriesData);

      // Fetch user favorites if logged in
      if (userId) {
        try {
          const [favoritesResponse, likesResponse] = await Promise.all([
            favoritesService.getFavorites(userId, { type: 'blog' }),
            favoritesService.getFavorites(userId, { type: 'blog-like' })
          ]);
          setUserFavorites(new Set(favoritesResponse.data?.map(favorite => favorite.itemId) || []));
          setUserLikes(new Set(likesResponse.data?.map(like => like.itemId) || []));
        } catch (error) {
          console.error('Error fetching user favorites:', error);
        }
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      setError('A apărut o eroare la încărcarea articolelor. Vă rugăm să încercați din nou.');
      toast.error('Eroare la încărcarea articolelor');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Filter and sort blogs
  const filteredBlogs = useMemo(() => {
    let filtered = [...blogs];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(blog =>
        blog.title?.toLowerCase().includes(searchLower) ||
        blog.content?.toLowerCase().includes(searchLower) ||
        blog.author?.toLowerCase().includes(searchLower) ||
        blog.category?.toLowerCase().includes(searchLower) ||
        blog.excerpt?.toLowerCase().includes(searchLower)
      );
    }

    // Apply category filter
    if (selectedCategory && selectedCategory !== 'all') {
      filtered = filtered.filter(blog => blog.category === selectedCategory);
    }

    // Apply date filter
    if (dateFilter && dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          filtered = filtered.filter(blog => new Date(blog.createDate) >= filterDate);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          filtered = filtered.filter(blog => new Date(blog.createDate) >= filterDate);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          filtered = filtered.filter(blog => new Date(blog.createDate) >= filterDate);
          break;
        case 'year':
          filterDate.setFullYear(now.getFullYear() - 1);
          filtered = filtered.filter(blog => new Date(blog.createDate) >= filterDate);
          break;
        default:
          break;
      }
    }

    // Apply reading time filter
    if (readingTimeFilter && readingTimeFilter !== 'all') {
      switch (readingTimeFilter) {
        case 'short':
          filtered = filtered.filter(blog => blog.readingTime <= 3);
          break;
        case 'medium':
          filtered = filtered.filter(blog => blog.readingTime > 3 && blog.readingTime <= 7);
          break;
        case 'long':
          filtered = filtered.filter(blog => blog.readingTime > 7);
          break;
        default:
          break;
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createDate) - new Date(a.createDate);
        case 'oldest':
          return new Date(a.createDate) - new Date(b.createDate);
        case 'popular':
          return (b.views + b.likes + b.comments) - (a.views + a.likes + a.comments);
        case 'title':
          return a.title.localeCompare(b.title, 'ro');
        case 'reading-time':
          return a.readingTime - b.readingTime;
        default:
          return 0;
      }
    });

    return filtered;
  }, [blogs, searchTerm, selectedCategory, dateFilter, readingTimeFilter, sortBy]);

  // Pagination
  const paginatedBlogs = useMemo(() => {
    const startIndex = (currentPage - 1) * blogsPerPage;
    return filteredBlogs.slice(startIndex, startIndex + blogsPerPage);
  }, [filteredBlogs, currentPage]);

  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);

  // Handle favorite toggle
  const handleFavoriteToggle = useCallback(async (blog) => {
    if (!userId) {
      toast.error('Trebuie să fiți autentificat pentru a adăuga la favorite');
      return;
    }

    try {
      const isFavorited = userFavorites.has(blog.id);
      const favoriteData = {
        userId,
        itemId: blog.id,
        itemType: 'blog',
        title: blog.title,
        description: blog.excerpt,
        category: blog.category,
        imageUrl: blog.image || blog.coverImage,
        url: `/blog/${blog.id}`
      };

      if (isFavorited) {
        await favoritesService.removeFavorite(userId, blog.id, 'blog');
        setUserFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(blog.id);
          return newSet;
        });
        toast.success('Articol eliminat din favorite');
      } else {
        await favoritesService.addFavorite(userId, favoriteData);
        setUserFavorites(prev => new Set([...prev, blog.id]));
        toast.success('Articol adăugat la favorite');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Eroare la actualizarea favoritelor');
    }
  }, [userId, userFavorites]);

  // Handle like toggle
    const handleLikeToggle = useCallback(async (blog) => {
    if (!isUserAuthenticated || !userId) {
      toast.error('Trebuie să fiți autentificat pentru a aprecia articolele');
      return;
    }

    try {
      const isLiked = userLikes.has(blog.id);
      
      // Update local state immediately for better UX
      setUserLikes(prev => {
        const newSet = new Set(prev);
        if (isLiked) {
          newSet.delete(blog.id);
        } else {
          newSet.add(blog.id);
        }
        return newSet;
      });

      // Update blog likes count
      setBlogs(prev => prev.map(b => 
        b.id === blog.id 
          ? { ...b, likes: isLiked ? Math.max(0, b.likes - 1) : b.likes + 1 }
          : b
      ));

      console.log('Attempting to toggle favorite with userId:', userId);

      // Use the toggle functionality from favorites service
      const result = await favoritesService.toggleFavorite(userId, {
        itemId: blog.id,
        itemType: 'blog-like',
        title: blog.title,
        description: blog.excerpt || blog.description || '',
        category: blog.category || ''
      });

      if (result) {
        toast.success('Articol apreciat!');
      } else {
        toast.success('Like eliminat');
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revert optimistic update
      setUserLikes(prev => {
        const newSet = new Set(prev);
        if (userLikes.has(blog.id)) {
          newSet.add(blog.id);
        } else {
          newSet.delete(blog.id);
        }
        return newSet;
      });
      toast.error('Eroare la actualizarea aprecierii');
    }
  }, [userLikes, userId, isUserAuthenticated]);

  // Handle share
  const handleShare = useCallback(async (blog) => {
    const shareUrl = `${window.location.origin}/blog/${blog.id}`;
    const shareData = {
      title: blog.title,
      text: blog.excerpt,
      url: shareUrl
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copiat în clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copiat în clipboard!');
      } catch (clipboardError) {
        toast.error('Eroare la partajare');
      }
    }
  }, []);

  // Handle delete blog (admin only)
  const handleDeleteBlog = useCallback(async (blogId, blogTitle) => {
    if (!isAdmin()) {
      toast.error('Nu aveți permisiunea de a șterge articole');
      return;
    }

    const confirmDelete = window.confirm(
      `Sunteți sigur că doriți să ștergeți articolul "${blogTitle}"?\n\nAceastă acțiune nu poate fi anulată.`
    );

    if (!confirmDelete) return;

    try {
      await blogService.deleteBlog(blogId);
      toast.success('Articolul a fost șters cu succes!');
      // Refresh the blogs list
      fetchData();
    } catch (error) {
      console.error('Error deleting blog:', error);
      toast.error('Eroare la ștergerea articolului: ' + error.message);
    }
  }, [isAdmin, fetchData]);

  // Clear filters
  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("all");
    setDateFilter("all");
    setReadingTimeFilter("all");
    setSortBy("newest");
    setCurrentPage(1);
  };

  // Effects
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredBlogs.length]);

  // Render loading state
  if (loading) {
    return (
      <div className="modern-blog-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <h3>Se încarcă articolele...</h3>
          <p>Pregătim conținutul pentru tine</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="modern-blog-container">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Oops! Ceva nu a mers bine</h3>
          <p>{error}</p>
          <button onClick={fetchData} className="retry-button">
            <FiRefreshCw />
            Încearcă din nou
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-blog-container">
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="blog-hero__layout">
          <div className="hero-content">
            <span className="blog-hero__pretitle">Jurnal de wellness</span>
            <h1 className="hero-title">
              <span>Descoperă lumea</span>
              <em>uleiurilor esențiale</em>
            </h1>
            <p className="hero-subtitle">
              Articole despre aromaterapie, ritualuri simple și alegeri naturale care aduc mai mult echilibru în fiecare zi.
            </p>
            <div className="hero-stats">
              <div className="hero-stat">
                <span className="stat-number">{blogs.length}</span>
                <span className="stat-label">Articole</span>
              </div>
              <div className="hero-stat">
                <span className="stat-number">{categories.length}</span>
                <span className="stat-label">Categorii</span>
              </div>
              {isAdmin() && (
                <div className="hero-stat">
                  <span className="stat-number">{blogs.reduce((sum, blog) => sum + (blog.likes || 0), 0)}</span>
                  <span className="stat-label">Aprecieri</span>
                </div>
              )}
              <div className="hero-stat">
                <span className="stat-number">{Math.round(blogs.reduce((sum, blog) => sum + (blog.readingTime || 0), 0) / Math.max(blogs.length, 1))}</span>
                <span className="stat-label">Min citire</span>
              </div>
            </div>
          </div>
          <div className="blog-hero__visual" aria-hidden="true">
            <div className="blog-hero__bottles">
              <img className="blog-hero__oil blog-hero__oil--lavender" src={lavenderOil} alt="" />
              <img className="blog-hero__oil blog-hero__oil--peppermint" src={peppermintOil} alt="" />
              <img className="blog-hero__oil blog-hero__oil--lemon" src={lemonOil} alt="" />
            </div>
            <span className="blog-hero__visual-label">Natură · ritual · echilibru</span>
          </div>
        </div>
      </section>

      <div className="container">
        {/* Search and Filters */}
        <section className="search-section">
          <div className="search-header">
            <h2 className="section-title">
              <FiSearch />
              Caută și descoperă
            </h2>
            <button 
              className={`filters-toggle ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
              aria-expanded={showFilters}
              aria-controls="blog-filters-panel"
            >
              <FiFilter aria-hidden="true" />
              Filtre avansate
            </button>
          </div>

          <div className="search-box">
            <FiSearch className="search-icon" aria-hidden="true" />
            <label className="blog-search__label" htmlFor="blog-search-input">
              Caută articole, categorii sau subiecte
            </label>
            <input
              id="blog-search-input"
              type="text"
              placeholder="Caută articole, categorii sau subiecte..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="clear-search"
                aria-label="Șterge căutarea"
              >
                <FiX aria-hidden="true" />
              </button>
            )}
          </div>

          <div
            id="blog-filters-panel"
            className={`filters-panel ${showFilters ? 'expanded' : ''}`}
          >
            <div className="filters-header">
              <h3 className="filters-title">Filtrare Avansată</h3>
              <p className="filters-subtitle">Personalizează experiența ta de citire</p>
            </div>
            
            <div className="filter-group">
              <label htmlFor="blog-category-filter"><FiTag className="filter-icon" />Categorie</label>
              <select 
                id="blog-category-filter"
                value={selectedCategory} 
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="filter-select"
              >
                <option value="all">Toate categoriile</option>
                {categories.map(category => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="blog-date-filter"><FiCalendar className="filter-icon" />Perioada</label>
              <select 
                id="blog-date-filter"
                value={dateFilter} 
                onChange={(e) => setDateFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">Oricând</option>
                <option value="today">Azi</option>
                <option value="week">Ultima săptămână</option>
                <option value="month">Ultima lună</option>
                <option value="year">Ultimul an</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="blog-reading-time-filter"><FiClock className="filter-icon" />Timp de citire</label>
              <select 
                id="blog-reading-time-filter"
                value={readingTimeFilter} 
                onChange={(e) => setReadingTimeFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">Orice durată</option>
                <option value="short">Sub 3 minute</option>
                <option value="medium">3-7 minute</option>
                <option value="long">Peste 7 minute</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="blog-sort-filter">Sortare</label>
              <select 
                id="blog-sort-filter"
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                className="filter-select"
              >
                <option value="newest">Cele mai noi</option>
                <option value="oldest">Cele mai vechi</option>
                <option value="popular">Cele mai populare</option>
                <option value="title">Titlu A-Z</option>
                <option value="reading-time">Timp de citire</option>
              </select>
            </div>

            <div className="filter-actions">
              {(searchTerm || selectedCategory !== 'all' || dateFilter !== 'all' || readingTimeFilter !== 'all') && (
                <button onClick={clearFilters} className="clear-filters">
                  <FiX />
                  Resetează Filtrele
                </button>
              )}
            </div>
          </div>

          <div className="results-info">
            <div className="results-count">
              <span className="count-number">{filteredBlogs.length}</span>
              <span className="count-text">
                {filteredBlogs.length === 1 ? 'articol găsit' : 'articole găsite'}
              </span>
              {searchTerm && (
                <span className="search-term">pentru "{searchTerm}"</span>
              )}
            </div>
            
            {/* Active Filters Display */}
            <div className="active-filters">
              {selectedCategory !== 'all' && (
                <div className="filter-badge category-filter-badge">
                  <FiTag />
                  <span>Categorie: {selectedCategory}</span>
                  <button 
                    className="remove-filter" 
                    onClick={() => setSelectedCategory('all')}
                    title="Elimină filtrul de categorie"
                  >
                    <FiX />
                  </button>
                </div>
              )}
              
              {(selectedCategory !== 'all' || dateFilter !== 'all' || readingTimeFilter !== 'all') && (
                <button className="clear-all-filters" onClick={clearFilters}>
                  <FiX />
                  Șterge toate
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="blogs-section">
          {paginatedBlogs.length === 0 ? (
            <div className="no-results">
              <div className="no-results-icon" aria-hidden="true"><FiSearch /></div>
              <h3>Nu am găsit articole</h3>
              <p>Încearcă să modifici criteriile de căutare sau filtrele aplicate</p>
              {(searchTerm || selectedCategory !== 'all' || dateFilter !== 'all' || readingTimeFilter !== 'all') && (
                <button onClick={clearFilters} className="clear-filters-button">
                  Șterge toate filtrele
                </button>
              )}
            </div>
          ) : (
            <div className="blog-grid">
              {paginatedBlogs.map((blog, index) => (
                <article key={blog.id} className="blog-card">
                  <div className="blog-card__media">
                    {isAdmin() && (
                      <div className="blog-card__admin-actions">
                        <Link
                          to={`/admin/blog/update/${blog.id}`}
                          className="main-page-text-edit__trigger blog-card__edit"
                          title="Editează articolul"
                          aria-label={`Editează articolul ${blog.title}`}
                        >
                          <FiEdit3 aria-hidden="true" />
                        </Link>
                        <button
                          type="button"
                          className="admin-delete-btn-list"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleDeleteBlog(blog.id, blog.title);
                          }}
                          title="Șterge articolul"
                          aria-label={`Șterge articolul ${blog.title}`}
                        >
                          <FiTrash2 aria-hidden="true" />
                        </button>
                      </div>
                    )}
                    <Link to={`/blog-single-fullwidth/${blog.id}`} className="blog-card-image-link">
                      <div className="blog-card-image">
                        <img
                          src={blog.image || blog.coverImage || '/api/placeholder/400/250'}
                          alt={blog.title}
                          onError={(e) => {
                            e.target.src = '/api/placeholder/400/250';
                          }}
                        />
                        <div className="card-overlay"></div>
                        <div className="reading-time-badge">
                          <FiClock />
                          {blog.readingTime} min
                        </div>
                      </div>
                    </Link>
                    <div className="quick-actions">
                      <button
                        onClick={() => handleLikeToggle(blog)}
                        className={`action-btn ${userLikes.has(blog.id) ? 'active' : ''}`}
                        title={userLikes.has(blog.id) ? 'Elimină aprecierea' : 'Apreciază'}
                        aria-label={userLikes.has(blog.id)
                          ? `Elimină aprecierea articolului ${blog.title}`
                          : `Apreciază articolul ${blog.title}`}
                        aria-pressed={userLikes.has(blog.id)}
                      >
                        <FiHeart aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => handleFavoriteToggle(blog)}
                        className={`action-btn ${userFavorites.has(blog.id) ? 'active' : ''}`}
                        title={userFavorites.has(blog.id) ? 'Elimină din favorite' : 'Salvează'}
                        aria-label={userFavorites.has(blog.id)
                          ? `Elimină articolul ${blog.title} din favorite`
                          : `Salvează articolul ${blog.title}`}
                        aria-pressed={userFavorites.has(blog.id)}
                      >
                        <FiBookmark aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => handleShare(blog)}
                        className="action-btn"
                        title="Partajează"
                        aria-label={`Partajează articolul ${blog.title}`}
                      >
                        <FiShare2 aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <div className="blog-card-content">
                    <Link to={`/blog-single-fullwidth/${blog.id}`} className="blog-title-link">
                      <h3 className="blog-title">{blog.title}</h3>
                    </Link>

                    <Link to={`/blog-single-fullwidth/${blog.id}`} className="blog-excerpt-link">
                      <p className="blog-excerpt" title="Citește articolul complet">
                        {blog.excerpt}
                      </p>
                    </Link>

                    <div className="blog-footer">
                      <div className="engagement-stats">
                        <button 
                          className={`stat like-btn ${userLikes.has(blog.id) ? 'liked' : ''}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleLikeToggle(blog);
                          }}
                          title={userLikes.has(blog.id) ? 'Elimină aprecierea' : 'Apreciază articolul'}
                          aria-label={userLikes.has(blog.id) ? 'Elimină aprecierea' : 'Apreciază articolul'}
                        >
                          <FiHeart />
                          {isAdmin() && <span>{blog.likes}</span>}
                        </button>
                        <span className="stat" title="Comentarii">
                          <FiMessageCircle />
                          {blog.comments}
                        </span>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* Pagination */}
        {totalPages > 1 && (
          <section className="pagination-section">
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="page-btn"
                title="Prima pagină"
              >
                <FiChevronLeft />
                <FiChevronLeft />
              </button>
              
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-btn"
                title="Pagina anterioară"
              >
                <FiChevronLeft />
              </button>

              {[...Array(Math.min(5, totalPages))].map((_, index) => {
                const pageNumber = Math.max(1, currentPage - 2) + index;
                if (pageNumber <= totalPages) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`page-btn ${currentPage === pageNumber ? 'active' : ''}`}
                    >
                      {pageNumber}
                    </button>
                  );
                }
                return null;
              })}

              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-btn"
                title="Pagina următoare"
              >
                <FiChevronRight />
              </button>
              
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="page-btn"
                title="Ultima pagină"
              >
                <FiChevronRight />
                <FiChevronRight />
              </button>
            </div>
            
            <div className="pagination-info">
              Pagina {currentPage} din {totalPages}
            </div>
          </section>
        )}

        {/* Back to top button */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="back-to-top"
          title="Înapoi în sus"
        >
          <FiArrowUp />
        </button>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user
});

export default connect(mapStateToProps)(ModernBlogList);
